/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/EventPreRegisterResultModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./index.module.css";

const SUCCESS_FIELD_GROUPS = [
  {
    sectionKey: "registration",
    titleKey: "pages.eventPreRegister.resultModal.formSection",
    fieldsKey: "registrationFields",
    fields: ["genero", "rangoEdad", "cargo", "pais", "estado", "ciudad"],
  },
  {
    sectionKey: "user",
    titleKey: "pages.eventPreRegister.profileGroups.user",
    fieldsKey: "userFields",
    fields: [
      "tipoVisitante",
      "nombre",
      "apellido1",
      "apellido2",
      "correoElectronico",
      "numeroCelular",
    ],
  },
  {
    sectionKey: "company",
    titleKey: "pages.eventPreRegister.profileGroups.company",
    fieldsKey: "companyFields",
    fields: [
      "rfcEmpresa",
      "nombreComercialEmpresa",
      "razonSocialEmpresa",
      "eresSocioCaintra",
      "numeroSocio",
      "sector",
      "giro",
      "tamanoEmpresa",
      "cantidadPersonalOcupado",
    ],
  },
  {
    sectionKey: "event",
    titleKey: "pages.eventPreRegister.resultModal.eventSection",
    fieldsKey: "eventFields",
    fields: ["interesado", "asistePor", "seEntero"],
  },
];

const PROFILE_FIELD_GROUPS = [
  { key: "user", fields: ["nombre", "correoElectronico", "numeroCelular"] },
  {
    key: "company",
    fields: [
      "RFC",
      "nombreComercial",
      "razonSocial",
      "numeroSocio",
      "sectorEconomico",
      "ramaSCIAN",
      "tamanoDeLaEmpresa",
    ],
  },
];

const ALL_PROFILE_FIELD_KEYS = PROFILE_FIELD_GROUPS.flatMap(
  (group) => group.fields,
);

const EventPreRegisterResultModal = ({
  open,
  onClose,
  variant,
  message,
  data,
}) => {
  const { t } = useTranslation();

  const missingFieldLabels = useMemo(() => {
    if (variant !== "error" || !Array.isArray(data?.fieldsMissing)) {
      return [];
    }

    return data.fieldsMissing
      .filter((fieldKey) => ALL_PROFILE_FIELD_KEYS.includes(fieldKey))
      .map((fieldKey) => {
        const group = PROFILE_FIELD_GROUPS.find((g) =>
          g.fields.includes(fieldKey),
        );
        return group
          ? t(`pages.eventPreRegister.profileFields.${group.key}.${fieldKey}`)
          : fieldKey;
      });
  }, [variant, data?.fieldsMissing, t]);

  if (!open) {
    return null;
  }

  const isSuccess = variant === "success";

  const getSuccessFieldLabel = (fieldsKey, fieldKey) =>
    t(`pages.eventPreRegister.resultModal.${fieldsKey}.${fieldKey}`);

  const renderDataRows = (fieldsKey, fieldKeys, source) => {
    if (!source || typeof source !== "object") {
      return null;
    }

    return fieldKeys
      .filter((fieldKey) => source[fieldKey] != null && source[fieldKey] !== "")
      .map((fieldKey) => (
        <div className={styles.dataRow} key={fieldKey}>
          <span className={styles.dataLabel}>
            {getSuccessFieldLabel(fieldsKey, fieldKey)}
          </span>
          <span className={styles.dataValue}>{source[fieldKey] ?? "—"}</span>
        </div>
      ));
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div
        className={styles.modalContent}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-pre-register-result-title"
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <CloseIcon fontSize="small" />
        </button>

        <div className={styles.modalHeader}>
          <h2
            id="event-pre-register-result-title"
            className={`${styles.modalTitle} ${
              isSuccess ? "" : styles.modalTitleError
            }`}
          >
            {isSuccess
              ? t("pages.eventPreRegister.resultModal.successTitle")
              : t("pages.eventPreRegister.resultModal.errorTitle")}
          </h2>
        </div>

        <div className={styles.modalBody}>
          {message && <p className={styles.modalMessage}>{message}</p>}

          {isSuccess &&
            data &&
            SUCCESS_FIELD_GROUPS.map((group) => (
              <div className={styles.dataSection} key={group.sectionKey}>
                <h3 className={styles.sectionTitle}>{t(group.titleKey)}</h3>
                {renderDataRows(group.fieldsKey, group.fields, data)}
              </div>
            ))}

          {!isSuccess && missingFieldLabels.length > 0 && (
            <div className={styles.dataSection}>
              <h3 className={styles.sectionTitle}>
                {t("pages.eventPreRegister.resultModal.missingFieldsSection")}
              </h3>
              <ul className={styles.missingFieldsList}>
                {missingFieldLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventPreRegisterResultModal;
